import {
  DATE_FILTER_STRING_FORMAT,
  DATE_TIME_FILTER_STRING_FORMAT,
  DateRange,
  LOGGED_IN_USER_FILTER_ARG,
  OperandData,
  RelativePeriod,
  SYSTEM_COLUMN_DISPLAY_NAME,
  SYSTEM_GENERATED_COLUMN_TYPES,
  TableColumnForTableExport,
  ThisPeriod,
  createDynamicValue,
  dateTimeOperandSchema,
  dynamicDateTimeRangeOperandSchema,
  getDataTypeForSystemGeneratedColumn,
  getFilterStringAndArrayFromOperandData,
  isDateColumn,
  isNumericColumn,
  isValidDate,
  normalizeNumberArrayIsAnyOfOperand,
  parseSystemColumnFieldId,
  partialStaticDateTimeRangeOperandSchema,
  relativePeriodDynamicValueSchema,
  thisPeriodDynamicValueSchema,
  validateFilterArg,
} from '@doss/common-all'
import { GridFilterItem, GridFilterModel, GridLogicOperator } from '@mui/x-data-grid-premium'
import dayjs from 'dayjs'
import {
  compact,
  find,
  first,
  get,
  isBoolean,
  isEmpty,
  isEqual,
  isNil,
  isUndefined,
  keyBy,
  remove,
} from 'lodash'
import {
  BooleanOperator,
  CreateTableColumnFilterInput,
  DataType,
  FilterOperator,
  IndexType,
  SystemGeneratedTableColumnType,
  TableColumnFilterInput,
  TableFilterInput,
  UpdateTableColumnFilterInput,
} from '~/common/generated/apollo-operations'
import {
  DetailedTableColumn,
  DetailedTableColumnFilter,
  DetailedTableTab,
  TableType,
} from './types'

// The date filter operators from DataGrid
export const DATE_FILTER_OPERATORS = ['after', 'onOrAfter', 'before', 'onOrBefore']

const VALUELESS_GRID_FILTER_OPERATORS = ['isEmpty', 'isNotEmpty', 'isDuplicate'] as const
const VALUELESS_GRID_FILTER_OPERATOR_SET = new Set<string>(VALUELESS_GRID_FILTER_OPERATORS)

// New interface for grid filter item to include tableId in order to handle multi-table filters
export interface DossGridFilterItem extends GridFilterItem {
  tableId: string
}

export interface DossGridFilterModel extends GridFilterModel {
  items: DossGridFilterItem[]
}

/** Column shape needed to resolve effective `DataType` for grid filter → API conversion. */
export type GridFilterColumnDataTypeSource = {
  id: string
  type: DataType
  resolvedSourceType?: DataType | null
  indexType?: IndexType | null
}

/**
 * Maps column ids to effective `DataType` for the DataGrid filter panel (table columns + system-generated).
 * Matches lookup resolution and numeric-ID columns used by `useTableActions` and bulk lookup refine.
 */
export function buildColumnDataTypeMapForGridFilters(
  columns: ReadonlyArray<GridFilterColumnDataTypeSource>,
): Record<string, DataType> {
  const acc: Record<string, DataType> = {}
  for (const tableColumn of columns) {
    let columnDataType: DataType
    if (tableColumn.type === DataType.LOOKUP) {
      columnDataType = tableColumn.resolvedSourceType as DataType
    } else {
      columnDataType = tableColumn.type
      const indexType = tableColumn.indexType
      if (columnDataType === DataType.ID && indexType === IndexType.NUMBER) {
        columnDataType = DataType.NUMBER
      }
    }
    acc[tableColumn.id] = columnDataType
  }
  SYSTEM_GENERATED_COLUMN_TYPES.forEach((systemType) => {
    const { dataType } = getDataTypeForSystemGeneratedColumn(systemType)
    acc[systemType] = dataType as DataType
  })
  return acc
}

/**
 * Converts grid filter items to `TableFilterInput[]` using operand + serialized filter string/array
 * (same path as table subscription / lazy row queries).
 */
export function dossGridFilterItemsToTableFilterInputs(
  items: DossGridFilterItem[],
  columnDataTypeMap: Record<string, DataType>,
): TableFilterInput[] {
  return compact(
    items.map((item) => {
      if (!item.operator || !item.field) {
        return undefined
      }
      if (
        getExpectedOperandTypeForOperatorString(item.operator) !== 'empty' &&
        (isUndefined(item.value) || item.value === '')
      ) {
        return undefined
      }
      const operator = getFilterOperatorFromString(item.operator)
      const columnDataType = get(columnDataTypeMap, item.field, undefined)
      if (!columnDataType) {
        return undefined
      }
      const operand = getFilterInputOperandFromGridFilterItem(operator, item, columnDataType)
      const { filterString, filterArray } = getFilterStringAndArrayFromOperandData(
        operand,
        columnDataType,
      )
      return {
        columnId: item.field,
        operator,
        operand,
        filterString,
        filterArray,
      }
    }),
  )
}

/**
 * Returns the DataGrid string filter operator from the FilterOperator on the table column filter
 */
export function getFilterOperatorFromString(stringOperator: string): FilterOperator {
  switch (stringOperator) {
    case 'contains':
      return FilterOperator.CONTAINS
    case 'doesNotContain':
      return FilterOperator.DOES_NOT_CONTAIN
    case 'equals':
    case '=':
    case 'is':
    case 'isOn':
      return FilterOperator.EQUALS
    case 'startsWith':
      return FilterOperator.STARTS_WITH
    case 'endsWith':
      return FilterOperator.ENDS_WITH
    case 'isEmpty':
      return FilterOperator.IS_EMPTY
    case 'isNotEmpty':
      return FilterOperator.IS_NOT_EMPTY
    case 'isDuplicate':
      return FilterOperator.IS_DUPLICATE
    case 'isAnyOf':
      return FilterOperator.IS_ANY_OF
    case 'isNoneOf':
      return FilterOperator.IS_NONE_OF
    case '!=':
    case 'not':
    case 'notOn':
      return FilterOperator.IS_NOT_EQUAL_TO
    case '>':
    case 'after':
      return FilterOperator.GREATER_THAN
    case '>=':
    case 'onOrAfter':
      return FilterOperator.GREATER_THAN_OR_EQUAL_TO
    case '<':
    case 'before':
      return FilterOperator.LESS_THAN
    case '<=':
    case 'onOrBefore':
      return FilterOperator.LESS_THAN_OR_EQUAL_TO
    case 'in':
      return FilterOperator.IN
    case 'notIn':
      return FilterOperator.NOT_IN
    case 'this':
    case 'last':
    case 'inTheNext':
    case 'between':
      return FilterOperator.RANGE
    case 'notInTheLast':
    case 'notBetween':
      return FilterOperator.NOT_IN_RANGE
    default:
      throw new Error(`Unknown filter operator: ${stringOperator}`)
  }
}

const VALUELESS_FILTER_OPERATORS = new Set<FilterOperator>(
  VALUELESS_GRID_FILTER_OPERATORS.map(getFilterOperatorFromString),
)

export const isValueLessFilterOperator = (operator: FilterOperator): boolean => {
  return VALUELESS_FILTER_OPERATORS.has(operator)
}

/**
 * Returns the expected operand type for the operator string, or null if the case is not defined
 *
 * This is used to determine whether to clear the operand value when the operator changes
 *
 * Not every case is defined, so the default is to clear the value
 */
function getExpectedOperandTypeForOperatorString(stringOperator: string): string | null {
  if (VALUELESS_GRID_FILTER_OPERATOR_SET.has(stringOperator)) {
    return 'empty'
  }

  switch (stringOperator) {
    case '=':
    case '!=':
    case '>':
    case '>=':
    case '<':
    case '<=':
      return 'number'
    case 'between':
    case 'notBetween':
      return 'static date'
    case 'this':
      return 'ThisPeriod'
    case 'last':
    case 'inTheNext':
    case 'notInTheLast':
      return 'RelativePeriod'
    default:
      return null
  }
}

/**
 * Determines whether to retain the current grid filter model value when the operator changes
 *
 * If the expected operand type is the same (e.g. string to string, relative period to relative period),
 * return false so that the current operator value stays in tact for a better UX.
 */
export function shouldClearOperandValueAfterOperatorChange(
  currentOperator: string,
  newOperator: string,
): boolean {
  const currentExpectedType = getExpectedOperandTypeForOperatorString(currentOperator)
  const newExpectedType = getExpectedOperandTypeForOperatorString(newOperator)
  if (!currentExpectedType || !newExpectedType) {
    // Not all cases are defined, so default to clearing the value
    return true
  }
  // If the expected operand type is different, clear the value, otherwise keep it
  return currentExpectedType !== newExpectedType
}

/**
 * Returns the input to create a table column filter from the DataGrid filter item
 */
export function generateFilterInput(
  tableColumn: Pick<
    DetailedTableColumn,
    'id' | 'type' | 'lookupColumn' | 'resolvedSourceType' | 'indexType'
  >,
  filterItem: DossGridFilterItem,
  tableTabId?: string,
): TableColumnFilterInput | CreateTableColumnFilterInput {
  const operator = getFilterOperatorFromString(filterItem.operator)
  const columnDataType = tableColumn.resolvedSourceType
  const operand = getFilterInputOperandFromGridFilterItem(operator, filterItem, columnDataType)

  return {
    tableColumnId: tableColumn.id,
    tableTabId,
    operator,
    operand,
  }
}

/**
 * Returns the input to create a table column filter for a system-generated column from the DataGrid filter item
 */
export function generateSystemColumnFilterInput(
  systemGeneratedColumnType: SystemGeneratedTableColumnType,
  filterItem: DossGridFilterItem,
  tableTabId?: string,
): TableColumnFilterInput | CreateTableColumnFilterInput {
  const operator = getFilterOperatorFromString(filterItem.operator)
  const { dataType: columnDataType } =
    getDataTypeForSystemGeneratedColumn(systemGeneratedColumnType)

  const operand = getFilterInputOperandFromGridFilterItem(
    operator,
    filterItem,
    columnDataType as DataType,
  )
  return {
    systemGeneratedColumnType,
    ...(tableTabId != null && { tableTabId }),
    operator,
    operand,
  }
}

/**
 * Given a DossGridFilterItem, the column type, and the operator, returns OperandData
 */
export function getFilterInputOperandFromGridFilterItem(
  operator: FilterOperator,
  filterItem: DossGridFilterItem,
  columnDataType: DataType,
): OperandData {
  switch (operator) {
    case FilterOperator.GREATER_THAN:
    case FilterOperator.GREATER_THAN_OR_EQUAL_TO:
    case FilterOperator.LESS_THAN:
    case FilterOperator.LESS_THAN_OR_EQUAL_TO:
      return {
        data:
          columnDataType === DataType.NUMBER || columnDataType === DataType.FORMULA_NUMBER
            ? Number(filterItem.value)
            : filterItem.value,
      }

    case FilterOperator.EQUALS:
    case FilterOperator.IS_NOT_EQUAL_TO: {
      if (columnDataType === DataType.BOOLEAN) {
        // When filter is first added or cleared, value may be undefined or empty string
        if (isNil(filterItem.value) || filterItem.value === '') {
          return { data: false }
        }
        return {
          data: JSON.parse(filterItem.value),
        }
      } else if (columnDataType === DataType.NUMBER || columnDataType === DataType.FORMULA_NUMBER) {
        return {
          data: Number(filterItem.value),
        }
      } else {
        return { data: filterItem.value }
      }
    }
    case FilterOperator.CONTAINS:
    case FilterOperator.DOES_NOT_CONTAIN:
    case FilterOperator.STARTS_WITH:
    case FilterOperator.ENDS_WITH:
    case FilterOperator.IN:
    case FilterOperator.NOT_IN:
      return {
        data: filterItem.value,
      }

    case FilterOperator.IS_ANY_OF:
    case FilterOperator.IS_NONE_OF: {
      if (filterItem.value === '' || isNil(filterItem.value)) {
        return { data: [] }
      }
      const values = filterItem.value as string[]
      let options: string[] | number[] = []
      const operandType = _typeFromColumnType(columnDataType)
      switch (operandType) {
        case 'number':
          options = values.map((value) => {
            return Number(value)
          })
          break
        case 'string':
          options = values
          break
        default:
          throw new Error(`Invalid operand type for filter ${operator}`)
      }
      return { data: options }
    }
    case FilterOperator.IS_EMPTY:
    case FilterOperator.IS_NOT_EMPTY:
    case FilterOperator.IS_DUPLICATE:
      return { data: null }
    case FilterOperator.RANGE:
    case FilterOperator.NOT_IN_RANGE: {
      // When you change the operator from a dynamic range to a static range, the value is undefined
      // This happens because the old dynamic value is cleared but the new static value is not yet selected.
      // Return an empty string as placeholder to prevent error from being thrown
      if (filterItem.value === undefined) {
        return {
          data: '',
        }
      }

      // If it is a dynamic range, the filterItem value is an object with a dynamic value object and a range based on the current timestamp
      // We only need to store the dynamic value in the database
      if (filterItem.value.dynamicValue) {
        // Convert class objects to plain json objects to allow deep equal comparison of new filter inputs and saved filters
        if (filterItem.value.dynamicValue instanceof ThisPeriod) {
          return {
            data: {
              dynamicValue: {
                type: filterItem.value.dynamicValue.type,
                unit: filterItem.value.dynamicValue.unit,
              },
            },
          }
        }
        if (filterItem.value.dynamicValue instanceof RelativePeriod) {
          return {
            data: {
              dynamicValue: {
                type: filterItem.value.dynamicValue.type,
                unit: filterItem.value.dynamicValue.unit,
                value: filterItem.value.dynamicValue.value,
                operator: filterItem.value.dynamicValue.operator,
                // Left out unless set, so unchanged filters still compare equal to saved ones
                ...(filterItem.value.dynamicValue.excludeCurrent
                  ? { excludeCurrent: filterItem.value.dynamicValue.excludeCurrent }
                  : {}),
              },
            },
          }
        }
        return {
          data: {
            dynamicValue: filterItem.value.dynamicValue,
          },
        }
      }

      // Static number range is not implemented at the moment (? confirm)
      // If it is a static DATE range, the filterItem value is an object with "from" and "to"
      // keys that contains dayjs objects. If it is a static DATETIME range, include time in the format.
      const staticRange = filterItem.value as DateRange
      const isDateTime =
        columnDataType === DataType.DATE_TIME || columnDataType === DataType.FORMULA_DATE_TIME
      const dateFormat = isDateTime ? DATE_TIME_FILTER_STRING_FORMAT : DATE_FILTER_STRING_FORMAT

      return {
        data: {
          lower: {
            data: staticRange.from ? staticRange.from.format(dateFormat) : '',
          },
          upper: {
            data: staticRange.to ? staticRange.to.format(dateFormat) : '',
          },
        },
      }
    }
    default:
      throw new Error(`Operator ${operator} is not supported by grid filters`)
  }
}

/**
 * Returns number for DataType.NUMBER, boolean for DataType.BOOLEAN,
 * and string for all other DataTypes
 */
function _typeFromColumnType(columnType: DataType): string {
  switch (columnType) {
    case DataType.ID:
    case DataType.STRING:
    case DataType.FILE_URL:
    case DataType.USER:
    case DataType.DATE:
    case DataType.DATE_TIME:
    case DataType.ENUM:
    case DataType.LOOKUP:
    case DataType.ARRAY:
    case DataType.FORMULA_STRING:
    case DataType.FORMULA_DATE:
    case DataType.FORMULA_DATE_TIME:
      return 'string'
    case DataType.NUMBER:
    case DataType.FORMULA_NUMBER:
      return 'number'
    case DataType.BOOLEAN:
      return 'boolean'
    // FORMULA_BOOLEAN is intentionally out of scope for the non-numeric-formulas epic (see
    // isFormula()'s doc comment in @doss/common-all's table-cell.ts) -- no cell-data schema or
    // resolver exists for it yet.
    case DataType.FORMULA_BOOLEAN:
      return ''
  }
}

/**
 * Given the table column filter operator and detailed column,
 * use the column data type to determine the appropriate grid filter operator string for Grid Filter Item
 */
export function getGridOperatorStringFromOperatorAndColumn(
  operator: FilterOperator,
  column: TableColumnForTableExport,
  operand?: unknown,
): string {
  const columnDataType = column.resolvedSourceType
  const columnIndexType = column.lookupColumn?.indexType ?? column.indexType
  const isNumeric = isNumericColumn(columnDataType, columnIndexType)
  switch (operator) {
    case FilterOperator.GREATER_THAN:
      if (isNumeric) {
        return '>'
      } else {
        return 'after'
      }
    case FilterOperator.GREATER_THAN_OR_EQUAL_TO:
      if (isNumeric) {
        return '>='
      } else {
        return 'onOrAfter'
      }
    case FilterOperator.LESS_THAN:
      if (isNumeric) {
        return '<'
      } else {
        return 'before'
      }
    case FilterOperator.LESS_THAN_OR_EQUAL_TO:
      if (isNumeric) {
        return '<='
      } else {
        return 'onOrBefore'
      }
    case FilterOperator.EQUALS:
      if (
        columnDataType === DataType.BOOLEAN ||
        columnDataType === DataType.ENUM ||
        columnDataType === DataType.DATE ||
        columnDataType === DataType.FORMULA_DATE ||
        columnDataType === DataType.USER
      ) {
        return 'is'
      } else if (
        columnDataType === DataType.DATE_TIME ||
        columnDataType === DataType.FORMULA_DATE_TIME
      ) {
        if (dateTimeOperandSchema.isValidSync(operand) && isValidDate(operand.data)) {
          return 'isOn'
        }
        return 'is'
      } else if (isNumeric) {
        return '='
      } else {
        return 'equals'
      }
    case FilterOperator.IS_NOT_EQUAL_TO: {
      if (isNumeric) {
        return '!='
      } else if (
        columnDataType === DataType.DATE_TIME ||
        columnDataType === DataType.FORMULA_DATE_TIME
      ) {
        if (dateTimeOperandSchema.isValidSync(operand) && isValidDate(operand.data)) {
          return 'notOn'
        }
        return 'not'
      } else {
        return 'not'
      }
    }
    case FilterOperator.CONTAINS:
      return 'contains'
    case FilterOperator.DOES_NOT_CONTAIN:
      return 'doesNotContain'
    case FilterOperator.STARTS_WITH:
      return 'startsWith'
    case FilterOperator.ENDS_WITH:
      return 'endsWith'
    case FilterOperator.IN:
      return 'in'
    case FilterOperator.NOT_IN:
      return 'notIn'
    case FilterOperator.IS_ANY_OF:
      return 'isAnyOf'
    case FilterOperator.IS_NONE_OF:
      return 'isNoneOf'
    case FilterOperator.IS_EMPTY:
      return 'isEmpty'
    case FilterOperator.IS_NOT_EMPTY:
      return 'isNotEmpty'
    case FilterOperator.IS_DUPLICATE:
      return 'isDuplicate'
    case FilterOperator.RANGE:
    case FilterOperator.NOT_IN_RANGE:
      if (isDateColumn(columnDataType)) {
        if (dynamicDateTimeRangeOperandSchema.isValidSync(operand)) {
          const { type, operator } = operand.data.dynamicValue
          if (type === 'this') {
            return 'this'
          } else if (!isNil(operator)) {
            return operator
          }
        }

        if (operator === FilterOperator.RANGE) {
          return 'between'
        }
        return 'notBetween'
      }
      throw new Error('Range filter not supported for non-date columns')
    default:
      throw new Error(`Operator ${operator} is not supported by grid filters`)
  }
}

/**
 * Checks if the filter input matches an existing TableColumnFilter
 */
export function isFilterSame(
  filterInput: TableColumnFilterInput | CreateTableColumnFilterInput,
  savedFilter: DetailedTableColumnFilter,
): boolean {
  // Dirty-check only: JSON may store numeric isAnyOf as string[]; grid may use number[].
  if (
    filterInput.operator !== savedFilter.operator ||
    !isEqual(
      normalizeNumberArrayIsAnyOfOperand(filterInput.operand),
      normalizeNumberArrayIsAnyOfOperand(savedFilter.operand),
    )
  ) {
    return false
  }
  if ('systemGeneratedColumnType' in filterInput && filterInput.systemGeneratedColumnType != null) {
    return savedFilter.systemGeneratedColumnType === filterInput.systemGeneratedColumnType
  }
  if (savedFilter.tableColumn != null && filterInput.tableColumnId != null) {
    return filterInput.tableColumnId === savedFilter.tableColumn.id
  }
  return false
}

function _tableColumnFilterToGridFilterItem(
  filter: DetailedTableColumnFilter,
  column: TableColumnForTableExport,
  tableId: string,
  fieldId: string,
): DossGridFilterItem | undefined {
  if (filter.tableColumn == null) {
    return undefined
  }
  const operand = filter.operand as Record<string, unknown>
  const filterArg = validateFilterArg({ ...filter, tableColumn: column })
  if (filterArg) {
    const value = operand.data
    return {
      tableId,
      field: fieldId,
      value,
      operator: getGridOperatorStringFromOperatorAndColumn(filter.operator, column, operand),
    }
  }
  return undefined
}

/**
 * Convert TableColumnFilters on the table tab to a DossGridFilterModel for the DataGrid component.
 * Supports both table column filters and system-generated column filters when tableType and tableId are provided.
 */
export function tableColumnFiltersToGridFilterModel(
  filters: DetailedTableColumnFilter[],
  columns: DetailedTableColumn[],
  booleanOperator: BooleanOperator | null,
  tableType?: TableType,
  tableId?: string,
): DossGridFilterModel {
  const gridFilters: (DossGridFilterItem | undefined)[] = []
  filters.forEach((filter) => {
    const filterColumn = filter.tableColumn
    if (filterColumn != null) {
      const column = find(columns, (tableColumn) => tableColumn.id === filterColumn.id)
      const tableIdForFilter = tableId ?? filterColumn.table.id
      if (column && tableIdForFilter) {
        gridFilters.push(
          _tableColumnFilterToGridFilterItem(filter, column, tableIdForFilter, column.id),
        )
      }
    } else if (filter.systemGeneratedColumnType != null && tableType != null && tableId != null) {
      const { dataType, indexType } = getDataTypeForSystemGeneratedColumn(
        filter.systemGeneratedColumnType,
      )
      const minimalColumn: TableColumnForTableExport = {
        id: filter.systemGeneratedColumnType,
        name: SYSTEM_COLUMN_DISPLAY_NAME[filter.systemGeneratedColumnType],
        type: dataType,
        innerType: null,
        indexType,
        resolvedSourceType: dataType,
        lookupColumn: null,
        order: 0,
      }
      const fieldId = filter.systemGeneratedColumnType
      gridFilters.push(_tableColumnFilterToGridFilterItem(filter, minimalColumn, tableId, fieldId))
    }
  })
  const model: DossGridFilterModel = {
    items: compact(gridFilters),
  }
  if (booleanOperator) {
    model.logicOperator =
      booleanOperator === BooleanOperator.AND ? GridLogicOperator.And : GridLogicOperator.Or
  }
  return model
}

/**
 * Given an MUI logic operator, converts to our own boolean operator type
 */
export function filterOperatorToBooleanOperator(
  operator: GridLogicOperator | undefined,
): BooleanOperator {
  if (!operator) {
    return BooleanOperator.AND
  }

  switch (operator) {
    case GridLogicOperator.And:
      return BooleanOperator.AND
    case GridLogicOperator.Or:
      return BooleanOperator.OR
  }
}

/**
 * Create input for update table column filters (including filters on system-generated columns).
 */
export function createUpdateTableColumnFiltersInput(
  selectedTab: DetailedTableTab,
  filterModel: DossGridFilterModel,
  tableColumns: DetailedTableColumn[],
  filterBooleanOperator?: GridLogicOperator,
) {
  const tabFilterIdsToDelete: string[] = []
  const tabFiltersToCreate: (TableColumnFilterInput | CreateTableColumnFilterInput)[] = []
  const tabFiltersToUpdate: UpdateTableColumnFilterInput[] = []
  const savedFilters = [...selectedTab.filters]

  const processItem = (
    filterItemAsTabFilterInput: TableColumnFilterInput | CreateTableColumnFilterInput,
    sameColumnPredicate: (savedFilter: DetailedTableColumnFilter) => boolean,
    isSystemColumn: boolean,
  ) => {
    const matchingSavedFilter = find(savedFilters, (savedFilter) =>
      isFilterSame(filterItemAsTabFilterInput, savedFilter),
    )
    if (isNil(matchingSavedFilter)) {
      const savedFiltersOnSameColumn = savedFilters.filter(sameColumnPredicate)
      if (isEmpty(savedFiltersOnSameColumn)) {
        tabFiltersToCreate.push(filterItemAsTabFilterInput)
      } else {
        const savedFilterToUpdate = first(savedFiltersOnSameColumn)
        if (savedFilterToUpdate) {
          const updatePayload: UpdateTableColumnFilterInput = {
            ...filterItemAsTabFilterInput,
            tableColumnFilterId: savedFilterToUpdate.id,
          }
          if (isSystemColumn) {
            updatePayload.tableColumnId = null
            updatePayload.systemGeneratedColumnType =
              filterItemAsTabFilterInput.systemGeneratedColumnType ?? undefined
          }
          tabFiltersToUpdate.push(updatePayload)
          remove(savedFilters, (savedFilter) => savedFilter.id === savedFilterToUpdate.id)
        } else {
          tabFiltersToCreate.push(filterItemAsTabFilterInput)
        }
      }
    } else {
      remove(savedFilters, (savedFilter) => savedFilter.id === matchingSavedFilter.id)
    }
  }

  filterModel.items.forEach((unsavedFilterItem) => {
    const column = tableColumns.find((tableColumn) => tableColumn.id === unsavedFilterItem.field)
    if (column) {
      processItem(
        generateFilterInput(column, unsavedFilterItem),
        (savedFilter) => savedFilter.tableColumn?.id === unsavedFilterItem.field,
        false,
      )
    } else {
      // If no column is found, check if it is a system-generated column
      const systemType = parseSystemColumnFieldId(unsavedFilterItem.field)
      if (systemType !== null) {
        processItem(
          generateSystemColumnFilterInput(
            systemType as SystemGeneratedTableColumnType,
            unsavedFilterItem,
          ),
          (savedFilter) => savedFilter.systemGeneratedColumnType === systemType,
          true,
        )
      }
    }
  })

  savedFilters.forEach((savedFilterToDelete) => {
    tabFilterIdsToDelete.push(savedFilterToDelete.id)
  })

  return {
    tableTabId: selectedTab.id,
    booleanOperator: filterOperatorToBooleanOperator(filterBooleanOperator),
    filtersToDelete: tabFilterIdsToDelete,
    filtersToCreate: tabFiltersToCreate,
    filtersToUpdate: tabFiltersToUpdate,
  }
}

type GetFiltersFromTableTabOptions = {
  selectedTab?: DetailedTableTab | null
  tableColumns?: TableColumnForTableExport[]
  tableId: string
}

/**
 * Given a selected table tab, returns the grid filter model and cleaned table filter inputs.
 * Pass `tableId` so system-generated column filters get the correct `tableId` on grid items.
 */
export function getFiltersFromTableTab({
  selectedTab,
  tableColumns,
  tableId,
}: GetFiltersFromTableTabOptions): {
  model: DossGridFilterModel
  cleanedFilters: TableFilterInput[]
} {
  const booleanOperator = selectedTab?.booleanOperator
  const gridFilters: DossGridFilterItem[] = []
  const tableFilterInputs: TableFilterInput[] = []
  const columnsById = keyBy(tableColumns ?? [], (column) => column.id)

  for (const filter of selectedTab?.filters ?? []) {
    const operand = filter.operand
    // Skip if filter has no table column id or system generated column type
    if (!filter.tableColumn && !filter.systemGeneratedColumnType) {
      // oxlint-disable-next-line no-console
      console.warn(`Skipping filter with no table column id or system generated column type`)
      continue
    }

    if (filter.tableColumn) {
      const column = get(columnsById, filter.tableColumn.id, undefined)
      if (!column) {
        // oxlint-disable-next-line no-console
        console.warn(`Skipping filter with invalid table column id: ${filter.tableColumn.id}`)
        continue
      }
      const filterArg = validateFilterArg({ ...filter, tableColumn: column })
      if (!filterArg) {
        // oxlint-disable-next-line no-console
        console.warn(`Skipping filter with invalid operand: ${JSON.stringify(operand)}`)
        continue
      }
      // Push the table filter input to be used in the backend
      tableFilterInputs.push({
        columnId: filterArg.columnId,
        operator: FilterOperator[filterArg.operator],
        operand: filterArg.operand,
        filterArray: filterArg.filterArray,
        filterString: filterArg.filterString,
      })
      // Push the grid filter item to be used in the frontend
      const gridFilterValue = convertOperandToGridFilterValue(operand as OperandData, column)
      gridFilters.push({
        tableId: filter.tableColumn.table.id,
        field: column.id,
        value: gridFilterValue,
        operator: getGridOperatorStringFromOperatorAndColumn(filter.operator, column, operand),
      })
    } else if (filter.systemGeneratedColumnType !== null) {
      const columnId = filter.systemGeneratedColumnType
      const { dataType, indexType } = getDataTypeForSystemGeneratedColumn(columnId)
      const filterArg = validateFilterArg({ ...filter, tableColumn: null })
      if (!filterArg) {
        // oxlint-disable-next-line no-console
        console.warn(`Skipping filter with invalid operand: ${JSON.stringify(operand)}`)
        continue
      }
      // Push the table filter input to be used in the backend
      tableFilterInputs.push({
        columnId: filterArg.columnId,
        operator: FilterOperator[filterArg.operator],
        operand: filterArg.operand,
        filterArray: filterArg.filterArray,
        filterString: filterArg.filterString,
      })
      const minimalColumn: TableColumnForTableExport = {
        id: columnId,
        name: SYSTEM_COLUMN_DISPLAY_NAME[columnId],
        type: dataType,
        resolvedSourceType: dataType,
        innerType: null,
        lookupColumn: null,
        indexType,
        order: 0,
      }
      const gridFilterValue = convertOperandToGridFilterValue(operand as OperandData, minimalColumn)
      gridFilters.push({
        tableId,
        field: columnId,
        value: gridFilterValue,
        operator: getGridOperatorStringFromOperatorAndColumn(
          filter.operator,
          minimalColumn,
          operand,
        ),
      })
    }
  }

  const cleanedFilters = compact(tableFilterInputs)
  const model: DossGridFilterModel = { items: gridFilters }
  if (booleanOperator) {
    model.logicOperator =
      booleanOperator === BooleanOperator.AND ? GridLogicOperator.And : GridLogicOperator.Or
  }
  return { model, cleanedFilters }
}

type GridFilterValue =
  | string
  | number
  | boolean
  | string[]
  | number[]
  | boolean[]
  | DateRange
  | { dynamicValue: ThisPeriod | RelativePeriod }
  | null

/**
 * Core logic to convert an operand to a DossGridFilterItem value
 */
export function convertOperandToGridFilterValue(
  operand: OperandData,
  column: TableColumnForTableExport,
): GridFilterValue {
  if (isBoolean(operand.data)) {
    const columnDataType = column.resolvedSourceType
    const { filterString } = getFilterStringAndArrayFromOperandData(operand, columnDataType)
    return filterString ?? null
  }

  // Static date range — partial states (one empty bound) are expected during
  // in-progress filter selection in the UI.
  if (partialStaticDateTimeRangeOperandSchema.isValidSync(operand)) {
    return {
      from: operand.data.lower.data ? dayjs(operand.data.lower.data) : undefined,
      to: operand.data.upper.data ? dayjs(operand.data.upper.data) : undefined,
    }
  }

  if (dynamicDateTimeRangeOperandSchema.isValidSync(operand)) {
    if (operand.data.dynamicValue.type === 'this') {
      const thisPeriod = thisPeriodDynamicValueSchema.validateSync(operand.data.dynamicValue)
      return {
        dynamicValue: createDynamicValue.this(thisPeriod.unit),
      }
    } else {
      const relativePeriod = relativePeriodDynamicValueSchema.validateSync(
        operand.data.dynamicValue,
      )
      return {
        dynamicValue: createDynamicValue.relative(
          relativePeriod.operator,
          relativePeriod.value,
          relativePeriod.unit,
          relativePeriod.excludeCurrent,
        ),
      }
    }
  }

  return operand.data ?? null
}

/**
 * For any LOGGED_IN_USER_FILTER_ARGS, swap in the logged in user's id
 */
export function swapLoggedInUserFilterArgForUserId(
  filters: TableFilterInput[],
  currentUserId?: string,
): TableFilterInput[] {
  if (!currentUserId) {
    return filters
  }
  return filters.map((filter) => {
    // If currently filtering on the currently logged in user, swap in their id
    if (filter.filterString === LOGGED_IN_USER_FILTER_ARG) {
      return { ...filter, filterString: currentUserId }
    } else if (filter.filterArray?.some((filter) => filter === LOGGED_IN_USER_FILTER_ARG)) {
      return {
        ...filter,
        filterArray: filter.filterArray.map((filter) =>
          filter === LOGGED_IN_USER_FILTER_ARG ? currentUserId : filter,
        ),
      }
    }
    return filter
  })
}

export const _privateTableFilters = {
  _typeFromColumnType,
}
